import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import Pagination from './common/Pagination';
import './IdleResourcesSection.css';

/**
 * IdleResourcesSection
 * ====================
 * Lists AWS resources in this account that have shown no meaningful activity
 * over the idle window (~3 months) so the team can decide what to clean up or
 * delete to save cost.
 *
 * A scheduled backend collector (idle_resources_collector_lambda, orchestrated
 * by a Step Functions state machine that scans accounts in batches) writes the
 * result to S3. This section reads the latest snapshot for the given team/env
 * from GET /api/idle-resources/{team}/{env} - no live scanning happens on read.
 */

const API_URL = getApiUrl();

// Icon per resource type (matched by a substring so variants like
// "EC2 Instance (idle)" / "EC2 Instance (stopped)" both resolve).
const TYPE_ICONS = [
  ['EBS Volume', '\uD83D\uDCBE'],      // 💾
  ['Elastic IP', '\uD83C\uDF10'],      // 🌐
  ['EC2', '\uD83D\uDDA5\uFE0F'],       // 🖥️
  ['RDS', '\uD83D\uDDC4\uFE0F'],       // 🗄️
  ['Snapshot', '\uD83D\uDCF8'],        // 📸
  ['Load Balancer', '\u2696\uFE0F'],   // ⚖️
  ['Lambda', '\u03BB'],                // λ
  ['DynamoDB', '\uD83D\uDCD1'],        // 📑
  ['NAT Gateway', '\uD83D\uDEA6'],     // 🚦
  ['Network Interface', '\uD83D\uDD0C'], // 🔌
  ['S3 Bucket', '\uD83E\uDEA3'],       // 🪣
  ['IAM', '\uD83D\uDC64'],             // 👤
  ['AMI', '\uD83D\uDCC0'],             // 💿
  ['ElastiCache', '\u26A1'],           // ⚡
  ['Redshift', '\uD83D\uDD34'],        // 🔴
  ['OpenSearch', '\uD83D\uDD0E'],      // 🔎
  ['ECS', '\uD83D\uDC33'],             // 🐳
  ['Security Group', '\uD83D\uDEE1\uFE0F'], // 🛡️
  ['Aurora', '\uD83C\uDF10'],          // 🌐 (Aurora cluster)
  ['Target Group', '\uD83C\uDFAF'],    // 🎯
  ['Auto Scaling Group', '\uD83D\uDCC8'], // 📈
  ['ACM', '\uD83D\uDD12'],             // 🔒 (ACM certificate)
  ['Certificate', '\uD83D\uDD12'],     // 🔒
  ['DMS', '\uD83D\uDD01'],             // 🔁 (DMS replication)
  ['Transfer Server', '\uD83D\uDCE1'], // 📡
  ['AppFlow', '\uD83D\uDD00'],         // 🔀
  ['CloudFormation', '\uD83D\uDCE6'],  // 📦
];

const typeIcon = (type) => {
  const hit = TYPE_ICONS.find(([key]) => (type || '').includes(key));
  return hit ? hit[1] : '\uD83D\uDD0D'; // 🔍
};

// Show this many rows per page; the numbered pager loads the next batch.
const PAGE_SIZE = 5;

// In the stack-wise view the unit of pagination is the STACK, not the resource:
// a page holds this many stacks, each expanding to its own idle resources.
// Paginating by resource there would split a stack across pages and make the
// "N of M idle" header meaningless. Stacks start collapsed, so a page can hold
// more of them than the flat view holds rows.
const GROUP_PAGE_SIZE = 10;

// Idle-age tabs. Each tab shows resources idle for AT LEAST its threshold
// (cumulative): 3m shows everything, 6m shows 6-months-and-older, 1y shows
// only the 1-year-and-older resources.
const AGE_TABS = [
  { key: '3m', label: '3 Months', minDays: 90 },
  { key: '6m', label: '6 Months', minDays: 180 },
  { key: '1y', label: '1 Year', minDays: 365 },
];

// Fallback catalog of the resource types the scan covers, used for the
// "Coverage" panel when the API response doesn't include supportedResourceTypes
// (e.g. before the first scan). The backend emits the authoritative list in
// `supportedResourceTypes`; this must stay roughly in sync with it.
const FALLBACK_SUPPORTED_TYPES = [
  { category: 'Compute', type: 'EC2 Instance (stopped)' },
  { category: 'Compute', type: 'EC2 Instance (idle)' },
  { category: 'Compute', type: 'Lambda Function' },
  { category: 'Compute', type: 'ECS Service (idle)' },
  { category: 'Compute', type: 'Auto Scaling Group (empty)' },
  { category: 'Storage', type: 'EBS Volume' },
  { category: 'Storage', type: 'EBS Snapshot (old)' },
  { category: 'Storage', type: 'EBS Snapshot (orphaned)' },
  { category: 'Storage', type: 'AMI (old)' },
  { category: 'Storage', type: 'S3 Bucket' },
  { category: 'Database', type: 'RDS Instance' },
  { category: 'Database', type: 'Aurora Cluster' },
  { category: 'Database', type: 'RDS Snapshot (old)' },
  { category: 'Database', type: 'RDS Cluster Snapshot (old)' },
  { category: 'Database', type: 'DynamoDB Table' },
  { category: 'Database', type: 'ElastiCache Cluster' },
  { category: 'Database', type: 'Redshift Cluster' },
  { category: 'Analytics', type: 'OpenSearch Domain' },
  { category: 'Networking', type: 'Elastic IP' },
  { category: 'Networking', type: 'Network Interface (ENI)' },
  { category: 'Networking', type: 'NAT Gateway' },
  { category: 'Networking', type: 'Load Balancer (application/network)' },
  { category: 'Networking', type: 'Load Balancer (classic)' },
  { category: 'Networking', type: 'Target Group (unused)' },
  { category: 'Networking', type: 'Security Group (unused)' },
  { category: 'Governance', type: 'CloudFormation Stack (idle)' },
  { category: 'Governance', type: 'CloudFormation Stack (failed)' },
  { category: 'Identity', type: 'IAM User' },
  { category: 'Security', type: 'ACM Certificate (expired/failed)' },
  { category: 'Migration', type: 'DMS Replication Instance (idle)' },
  { category: 'Migration', type: 'DMS Replication Task (stopped/failed)' },
  { category: 'Transfer', type: 'Transfer Server (offline)' },
  { category: 'Integration', type: 'AppFlow Flow (inactive)' },
];

// Normalise a resource's stack provenance. Snapshots collected before the
// backend started emitting `provenance` have no such key, so managedBy falls
// back to 'unknown' and every field is defaulted rather than read blindly.
const provenanceOf = (r) => {
  const p = (r && r.provenance) || {};
  return {
    managedBy: p.managedBy || 'unknown',
    stackName: p.stackName || null,
    stackId: p.stackId || null,
    logicalId: p.logicalId || null,
    cdkPath: p.cdkPath || null,
  };
};

// Short human label for a provenance object, used by the Source badge and the
// group-by-source headers. CloudFormation/CDK resources show their stack name
// when known; manual and unknown resources get a fixed label.
const provenanceLabel = (p) => {
  if (p.managedBy === 'cloudformation' || p.managedBy === 'cdk') {
    return p.stackName ? `Stack: ${p.stackName}` : 'Stack';
  }
  if (p.managedBy === 'manual') return 'Manual';
  return 'Unknown';
};

// Map a provenance object to its Source badge modifier class.
const sourceBadgeClass = (p) => {
  if (p.managedBy === 'cloudformation' || p.managedBy === 'cdk') return 'source-stack';
  if (p.managedBy === 'manual') return 'source-manual';
  return 'source-unknown';
};


// Build a CSV string from the idle-resource rows. Values are quoted/escaped so
// commas, quotes, and newlines inside a field never break the layout.
const buildCsv = (rows) => {
  const headers = ['Type', 'Resource Name', 'Resource ID', 'Region', 'Why idle', 'Idle since (days)', 'Detail', 'Source'];
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    const p = provenanceOf(r);
    lines.push([
      esc(r.resourceType),
      esc(r.name || r.resourceId),
      esc(r.resourceId),
      esc(r.region),
      esc(r.reason),
      esc(typeof r.idleSinceDays === 'number' ? r.idleSinceDays : ''),
      esc(r.detail),
      esc(p.stackName || p.managedBy),
    ].join(','));
  }
  return lines.join('\n');
};

// Trigger a client-side file download for the given text content.
const downloadFile = (filename, text, mime = 'text/csv') => {
  const blob = new Blob([text], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Human "idle since" label. Returns null when the resource type has no
// timestamp available from AWS (e.g. Elastic IP / ENI / Security Group /
// Target Group) so the caller can render an explanatory "n/a" instead.
const formatIdleSince = (days) => {
  if (days == null) return null;
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} mo${months > 1 ? 's' : ''} (${days}d)`;
  }
  return `${days}d`;
};

const IdleResourcesSection = ({ team, environment }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [ageTab, setAgeTab] = useState('3m');
  const [groupBy, setGroupBy] = useState('none');
  const [page, setPage] = useState(1);
  const [groupPage, setGroupPage] = useState(1);
  // Keys of the stacks whose resources are open. Empty = every stack collapsed,
  // which is the default: the stack-wise view is a list of stacks first, and
  // the resources appear when a stack is clicked.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [showCoverage, setShowCoverage] = useState(false);
  const [showCleanStacks, setShowCleanStacks] = useState(false);

  const fetchIdle = useCallback(async () => {
    if (!team || !environment) return;
    setLoading(true);
    setError(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.get(
        `${API_URL}${API_ENDPOINTS.idleResources(team, environment)}`
      );
      setData(res.data || null);
    } catch (err) {
      console.error('Error fetching idle resources:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load idle resources');
    } finally {
      setLoading(false);
    }
  }, [team, environment]);

  useEffect(() => {
    fetchIdle();
  }, [fetchIdle]);

  const resources = useMemo(
    () => (Array.isArray(data?.resources) ? data.resources : []),
    [data]
  );

  const resourceTypes = useMemo(() => {
    const set = new Set(resources.map((r) => r.resourceType).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [resources]);

  // Idle findings per stack name, counted from the resource list itself rather
  // than taken from the backend's own per-stack tally. This is the numerator the
  // headers display, so the "N of M idle" figure can never contradict the rows
  // listed underneath it: both come from the same array. Counted over ALL
  // resources, not the filtered ones, so the number describes the stack rather
  // than the active tab.
  const idleCountByStack = useMemo(() => {
    const byName = new Map();
    for (const r of resources) {
      // The stack's own finding is not one of its members.
      if ((r.resourceType || '').startsWith('CloudFormation Stack')) continue;
      const name = provenanceOf(r).stackName;
      if (!name) continue;
      byName.set(name, (byName.get(name) || 0) + 1);
    }
    return byName;
  }, [resources]);

  // Per-stack resource totals from the snapshot - the DENOMINATOR of "N of M
  // resources idle", which only the collector can know since it enumerates the
  // stack. Keyed by stack name because that is all a resource's provenance tag
  // carries. The same name can exist in more than one region, so those entries
  // are merged - otherwise one region's totals would silently stand for both.
  // Snapshots collected before the backend emitted `stacks` yield an empty map,
  // and the headers then show a bare idle count with no denominator.
  const stackStats = useMemo(() => {
    const list = Array.isArray(data?.stacks) ? data.stacks : [];
    const byName = new Map();
    for (const s of list) {
      const name = s && s.stackName;
      if (!name) continue;
      const cur = byName.get(name) || {
        stackName: name,
        regions: [],
        total: 0,
        assessable: 0,
        idle: 0,
        allIdle: false,
        truncated: false,
      };
      if (s.region && !cur.regions.includes(s.region)) cur.regions.push(s.region);
      cur.total += s.totalResources || 0;
      cur.assessable += s.assessableResources || 0;
      cur.idle += s.idleResources || 0;
      cur.allIdle = cur.allIdle || !!s.allIdle;
      cur.truncated = cur.truncated || !!s.truncated;
      byName.set(name, cur);
    }
    return byName;
  }, [data]);

  const stackSummary = data?.stackSummary || null;

  // How many resources fall in each age tab (cumulative "idle >= threshold").
  // A resource with unknown idle age (idleSinceDays == null) can only be placed
  // in the 3m tab, so it is excluded from 6m / 1y counts.
  const ageCounts = useMemo(() => {
    const counts = { '3m': 0, '6m': 0, '1y': 0 };
    for (const r of resources) {
      const d = r.idleSinceDays;
      counts['3m'] += 1; // everything flagged is idle >= 3 months
      if (typeof d === 'number' && d >= 180) counts['6m'] += 1;
      if (typeof d === 'number' && d >= 365) counts['1y'] += 1;
    }
    return counts;
  }, [resources]);

  const visibleResources = useMemo(() => {
    const minDays = (AGE_TABS.find((t) => t.key === ageTab) || AGE_TABS[0]).minDays;
    return resources.filter((r) => {
      // Age filter (cumulative). 3m keeps all; 6m/1y require a known age >= threshold.
      if (minDays > 90) {
        const d = r.idleSinceDays;
        if (typeof d !== 'number' || d < minDays) return false;
      }
      // Type filter
      if (typeFilter !== 'all' && r.resourceType !== typeFilter) return false;
      return true;
    });
  }, [resources, typeFilter, ageTab]);

  // Reset to the first page whenever a filter, the tab, or the underlying data
  // changes, so we never land on a page that no longer exists.
  useEffect(() => {
    setPage(1);
    setGroupPage(1);
    setExpandedGroups(new Set());
  }, [typeFilter, ageTab, groupBy, data]);

  const totalFiltered = visibleResources.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedResources = useMemo(
    () => visibleResources.slice(pageStart, pageStart + PAGE_SIZE),
    [visibleResources, pageStart]
  );

  // Display metrics for one stack name: how many of its resources this snapshot
  // flags idle, the stack's resource total from the inventory, the resulting
  // share, and whether the stack is idle as a WHOLE. `fullyIdle` is true either
  // when every enumerated resource is idle, or when the collector proved every
  // resource it can assess is idle - a stack can be completely dead at 67% of
  // its total, because IAM roles and log groups count towards that total but
  // can never be flagged. Shared by the sort and the header so the order always
  // matches what the headers show.
  const stackMetrics = useCallback((stackName) => {
    const stat = stackStats.get(stackName) || null;
    const idle = idleCountByStack.get(stackName) || 0;
    const total = stat ? stat.total : 0;
    const pct = total ? Math.min(100, Math.round((idle / total) * 100)) : null;
    return { stat, idle, total, pct, fullyIdle: !!(stat && stat.allIdle) || pct === 100 };
  }, [stackStats, idleCountByStack]);

  // Group the already-filtered resources by stack for the stack-wise view.
  // CloudFormation/CDK resources cluster under their stack name; everything
  // else lands in the Manual or Unknown group, which sort last so real stacks
  // lead the list.
  const sourceGroups = useMemo(() => {
    const byKey = new Map();
    for (const r of visibleResources) {
      const p = provenanceOf(r);
      let key;
      let label;
      let stackName = null;
      if (p.managedBy === 'manual') {
        key = '\uFFF0manual';
        label = 'Manual';
      } else if (p.managedBy === 'unknown') {
        key = '\uFFF1unknown';
        label = 'Unknown';
      } else {
        key = p.stackName ? `stack:${p.stackName}` : '\uFFEFstack';
        label = provenanceLabel(p);
        stackName = p.stackName;
      }
      if (!byKey.has(key)) byKey.set(key, { key, label, stackName, items: [] });
      byKey.get(key).items.push(r);
    }
    // Worst-first: fully idle stacks lead, then by idle share descending, so
    // the stacks worth deleting outright sit on page 1. Stacks the scan has no
    // inventory for have no share to compare and fall below the ranked ones;
    // Manual and Unknown are not stacks at all and stay at the very end.
    return Array.from(byKey.values()).sort((a, b) => {
      const aLast = !a.stackName;
      const bLast = !b.stackName;
      if (aLast !== bLast) return aLast ? 1 : -1;
      if (aLast) return a.key.localeCompare(b.key);
      const ma = stackMetrics(a.stackName);
      const mb = stackMetrics(b.stackName);
      const sa = ma.fullyIdle ? 100 : (ma.pct == null ? -1 : ma.pct);
      const sb = mb.fullyIdle ? 100 : (mb.pct == null ? -1 : mb.pct);
      if (sa !== sb) return sb - sa;
      if (ma.idle !== mb.idle) return mb.idle - ma.idle;
      return a.stackName.localeCompare(b.stackName);
    });
  }, [visibleResources, stackMetrics]);

  // Stack-wise pagination: a page is GROUP_PAGE_SIZE stacks, each expanding in
  // place, so a stack's resources are never split across pages.
  const groupPageCount = Math.max(1, Math.ceil(sourceGroups.length / GROUP_PAGE_SIZE));
  const currentGroupPage = Math.min(groupPage, groupPageCount);
  const groupStart = (currentGroupPage - 1) * GROUP_PAGE_SIZE;
  const pagedGroups = useMemo(
    () => sourceGroups.slice(groupStart, groupStart + GROUP_PAGE_SIZE),
    [sourceGroups, groupStart]
  );

  // Stacks the scan found nothing idle in. Listed outside the table, so they
  // stay visible as proof a stack is clean without consuming pages that should
  // be showing resources to act on.
  const cleanStacks = useMemo(() => {
    if (!stackStats.size) return [];
    return Array.from(stackStats.values())
      .filter((s) => !idleCountByStack.get(s.stackName))
      .sort((a, b) => b.total - a.total || a.stackName.localeCompare(b.stackName));
  }, [stackStats, idleCountByStack]);

  // When this list was collected from the AWS account. The per-account snapshot
  // uses `collectedAt`; the global index uses `lastUpdated`. Use whichever exists.
  const scannedRaw = data?.collectedAt || data?.lastUpdated || null;
  const scannedAt = scannedRaw ? new Date(scannedRaw) : null;

  // Export the currently-filtered idle resources (respects the age tab + type
  // filter) to a CSV the user can open in Excel. Filename encodes team/env/tab
  // and the scan date so saved files are self-describing.
  const handleDownloadCsv = useCallback(() => {
    if (!visibleResources.length) return;
    const datePart = (scannedRaw ? new Date(scannedRaw) : new Date())
      .toISOString().slice(0, 10);
    const typePart = typeFilter !== 'all'
      ? `-${typeFilter.replace(/[^a-z0-9]+/gi, '_')}`
      : '';
    const filename = `idle-resources-${team}-${environment}-${ageTab}${typePart}-${datePart}.csv`;
    downloadFile(filename, buildCsv(visibleResources));
  }, [visibleResources, team, environment, ageTab, typeFilter, scannedRaw]);

  // Coverage catalog ("what we handle"): prefer the backend-emitted list so it
  // always matches what the collector actually scans; fall back to the built-in
  // list before the first scan. Group by category for display.
  const coverageByCategory = useMemo(() => {
    const list = (Array.isArray(data?.supportedResourceTypes) && data.supportedResourceTypes.length)
      ? data.supportedResourceTypes
      : FALLBACK_SUPPORTED_TYPES;
    const groups = {};
    for (const item of list) {
      const cat = item.category || 'Other';
      (groups[cat] = groups[cat] || []).push(item);
    }
    return groups;
  }, [data]);
  const coverageCount = useMemo(
    () => Object.values(coverageByCategory).reduce((n, arr) => n + arr.length, 0),
    [coverageByCategory]
  );

  // Accordion head for one stack in the stack-wise view, collapsed until
  // clicked: the expand control carrying the stack name, the stack's idle
  // coverage ("12 of 87 resources idle (14%)") with a share bar and region, and
  // how many resources it will reveal. The idle count comes from the findings
  // themselves and the total from the scan's stack inventory, so a missing
  // inventory entry degrades to a bare count rather than a wrong ratio.
  const renderGroupHeader = (g) => {
    const expanded = expandedGroups.has(g.key);
    const shown = g.items.length;
    const toggle = () => setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g.key)) next.delete(g.key);
      else next.add(g.key);
      return next;
    });
    const toggleTitle = expanded
      ? 'Hide this group\u2019s resources'
      : `Show the ${shown} idle resource${shown !== 1 ? 's' : ''} in this group`;

    // Manual / Unknown clusters have no stack behind them, so there is no
    // resource total to measure against - they keep the plain label and count.
    if (!g.stackName) {
      return (
        <tr className="idle-group-row" key={`${g.key}-head`}>
          <td className="idle-group-header" colSpan={6}>
            <button
              type="button"
              className="idle-group-toggle"
              onClick={toggle}
              aria-expanded={expanded}
              title={toggleTitle}
            >
              {expanded ? '▾' : '▸'} {g.label}
            </button>
            <span className="idle-tab-count">{shown}</span>
          </td>
        </tr>
      );
    }

    const { stat, idle: idleHere, total, fullyIdle } = stackMetrics(g.stackName);
    const pct = total ? Math.min(100, Math.round((idleHere / total) * 100)) : 0;
    const ratioTitle = stat
      ? [
          `${idleHere} of ${stat.assessable} resources this scan can assess are idle`,
          'IAM roles, log groups, SSM parameters and nested stacks carry no idle signal, so they count towards the stack total but can never be flagged.',
          stat.truncated ? 'This stack has more resources than the scan enumerates, so its totals are partial.' : null,
          stat.idle !== idleHere
            ? `Note: the scan's own tally for this stack was ${stat.idle}. The count shown is taken from the findings listed here.`
            : null,
        ].filter(Boolean).join('\n')
      : 'The scan has no resource inventory for this stack, so only the idle count is known.';

    return (
      <tr className="idle-group-row" key={`${g.key}-head`}>
        <td className="idle-group-header" colSpan={6}>
          <button
            type="button"
            className="idle-group-toggle"
            onClick={toggle}
            aria-expanded={expanded}
            title={toggleTitle}
          >
            {expanded ? '▾' : '▸'} <span className="idle-stack-name">📦 {g.stackName}</span>
          </button>
          <span className="idle-stack-sep">·</span>
          <span className={`idle-stack-ratio${fullyIdle ? ' full' : ''}`} title={ratioTitle}>
            {total
              // A single idle resource in a 452-resource stack rounds to 0%,
              // which reads as "none"; show <1% instead.
              ? `${idleHere} of ${total} resources idle (${pct === 0 && idleHere > 0 ? '<1' : pct}%)`
              : `${idleHere} idle`}
          </span>
          {total > 0 && (
            <span className="idle-stack-bar" aria-hidden="true">
              <span
                className={`idle-stack-bar-fill${fullyIdle ? ' full' : ''}`}
                style={{ width: `${pct}%` }}
              />
            </span>
          )}
          {stat && stat.regions.length > 0 && (
            <>
              <span className="idle-stack-sep">·</span>
              <span className="idle-stack-region">{stat.regions.join(', ')}</span>
            </>
          )}
          {fullyIdle && (
            <span
              className="idle-stack-allidle"
              title={
                'Every resource in this stack that the scan can assess is idle - the whole stack is a cleanup candidate.'
                + (stat && stat.assessable && stat.assessable !== total
                  ? `\nThat is all ${stat.assessable} assessable resource(s); the remaining ${total - stat.assessable} are IAM roles, log groups and similar, which carry no idle signal.`
                  : '')
              }
            >
              100% idle
            </span>
          )}
          <span className="idle-tab-count" title="Idle resources in this stack matching the current filters">
            {shown}
          </span>
        </td>
      </tr>
    );
  };

  // One idle-resource table row, shared by the flat paginated view and the
  // grouped "By source" view so both stay in sync.
  const renderRow = (r, key) => {
    const p = provenanceOf(r);
    const badgeTitle = [
      p.stackId && `Stack ID: ${p.stackId}`,
      p.logicalId && `Logical ID: ${p.logicalId}`,
      p.cdkPath && `CDK path: ${p.cdkPath}`,
    ].filter(Boolean).join('\n');
    return (
      <tr key={key}>
        <td className="idle-type-cell" data-label="Type">
          <span className="idle-type-badge">
            {typeIcon(r.resourceType)} {r.resourceType}
          </span>
        </td>
        <td className="idle-resource-cell" data-label="Resource">
          <span className="idle-resource-name">{r.name || r.resourceId}</span>
          <code className="idle-resource-id">{r.resourceId}</code>
          {r.detail && <span className="idle-resource-detail">{r.detail}</span>}
        </td>
        <td className="idle-source-cell" data-label="Source">
          <span
            className={`idle-type-badge idle-source-badge ${sourceBadgeClass(p)}`}
            title={badgeTitle || undefined}
          >
            {provenanceLabel(p)}
          </span>
        </td>
        <td className="idle-region-cell" data-label="Region">{r.region}</td>
        <td className="idle-reason-cell" data-label="Why idle">{r.reason}</td>
        <td className="idle-since-cell" data-label="Idle since">
          {formatIdleSince(r.idleSinceDays) || (
            <span
              className="idle-since-na"
              title="AWS provides no allocation/creation timestamp for this resource type, so an exact idle duration is not available. It is unused now."
            >
              n/a
            </span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <section className="section-container idle-section">
      <h3 className="section-title">
        🧹 Idle Resources
        <span className="idle-count-badge" title="Idle resources found in this account">
          {resources.length} found
        </span>
        {scannedAt && (
          <span className="idle-scanned-badge" title="When this list was collected from the AWS account">
            📅 Scanned {scannedAt.toLocaleString()}
          </span>
        )}
      </h3>

      <p className="section-description">
        AWS resources in {team} ({(environment || '').toUpperCase()}) with no
        meaningful activity. Use the tabs to see resources idle for 3+ months,
        6+ months, or 1+ year. Review these for cleanup or deletion.
        {' '}
        <button
          type="button"
          className="idle-coverage-toggle"
          onClick={() => setShowCoverage((v) => !v)}
          aria-expanded={showCoverage}
        >
          {showCoverage ? 'Hide' : 'Show'} covered resource types ({coverageCount})
        </button>
      </p>

      {showCoverage && (
        <div className="idle-coverage">
          <div className="idle-coverage-head">
            This scan checks {coverageCount} resource types across your account:
          </div>
          <div className="idle-coverage-grid">
            {Object.keys(coverageByCategory).sort().map((cat) => (
              <div key={cat} className="idle-coverage-group">
                <div className="idle-coverage-cat">{cat}</div>
                <ul>
                  {coverageByCategory[cat].map((item) => (
                    <li key={item.type} title={item.signal || ''}>
                      <span className="idle-coverage-icon">{typeIcon(item.type)}</span>
                      <span className="idle-coverage-type">{item.type}</span>
                      {item.basis && (
                        <span className={`idle-coverage-basis basis-${item.basis}`}>{item.basis}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="idle-loading"><div className="spinner-small" /><span>Scanning results…</span></div>
      ) : error && !data ? (
        <div className="idle-error">
          <span>⚠️ {error}</span>
          <button onClick={fetchIdle}>Retry</button>
        </div>
      ) : resources.length === 0 ? (
        <div className="idle-empty">
          <span className="idle-empty-icon">✅</span>
          <p>No idle resources detected in this account. Nothing to clean up right now.</p>
        </div>
      ) : (
        <>
          <div className="idle-tabs" role="tablist">
            {AGE_TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={ageTab === t.key}
                className={`idle-tab ${ageTab === t.key ? 'active' : ''}`}
                onClick={() => setAgeTab(t.key)}
              >
                {t.label}
                <span className="idle-tab-count">{ageCounts[t.key]}</span>
              </button>
            ))}
          </div>

          <div className="idle-toolbar">
            <div className="idle-summary">
              <span className="idle-stat">
                <strong>{totalFiltered}</strong> resource{totalFiltered !== 1 ? 's' : ''} idle {ageTab === '3m' ? '3+ months' : ageTab === '6m' ? '6+ months' : '1+ year'}
              </span>
              {groupBy === 'source' && stackSummary && stackSummary.stackCount > 0 && (
                <span
                  className="idle-stat idle-stat-stacks"
                  title="Stacks holding at least one idle resource, out of all CloudFormation/CDK stacks found in this account"
                >
                  <strong>{idleCountByStack.size}</strong> of {stackSummary.stackCount} stack
                  {stackSummary.stackCount !== 1 ? 's' : ''} affected
                  {stackSummary.fullyIdleStacks > 0 && (
                    <> · <strong>{stackSummary.fullyIdleStacks}</strong> fully idle</>
                  )}
                </span>
              )}
            </div>
            <div className="idle-filter">
              <label htmlFor="idle-type-filter">Type:</label>
              <select
                id="idle-type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {resourceTypes.map((t) => (
                  <option key={t} value={t}>{t === 'all' ? 'All types' : t}</option>
                ))}
              </select>
              <label htmlFor="idle-group-by">Group:</label>
              <select
                id="idle-group-by"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <option value="none">No grouping</option>
                <option value="source">Stack-wise</option>
              </select>
              <button
                type="button"
                className="idle-download-btn"
                onClick={handleDownloadCsv}
                disabled={totalFiltered === 0}
                title="Download the resources shown here as a CSV file"
              >
                ⬇ Download CSV
              </button>
            </div>
          </div>

          {totalFiltered === 0 ? (
            <div className="idle-empty">
              <span className="idle-empty-icon">✅</span>
              <p>
                No resources idle{' '}
                {ageTab === '6m' ? 'for 6+ months' : ageTab === '1y' ? 'for 1+ year' : '3+ months'}
                {typeFilter !== 'all' ? ` for type “${typeFilter}”` : ''}.
              </p>
            </div>
          ) : (
          <div className="idle-list">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Resource</th>
                  <th className="idle-source-cell">Source</th>
                  <th className="idle-region-cell">Region</th>
                  <th>Why idle</th>
                  <th>Idle since</th>
                </tr>
              </thead>
              <tbody>
                {groupBy === 'source'
                  ? pagedGroups.map((g) => (
                      <React.Fragment key={g.key}>
                        {renderGroupHeader(g)}
                        {expandedGroups.has(g.key)
                          && g.items.map((r, i) => renderRow(r, `${g.key}-${i}`))}
                      </React.Fragment>
                    ))
                  : pagedResources.map((r, i) => renderRow(r, `${r.resourceId}-${pageStart + i}`))}
              </tbody>
            </table>
          </div>
          )}

          {groupBy === 'source' ? (
            totalFiltered > 0 && (
              <>
                <Pagination
                  page={currentGroupPage}
                  pageCount={groupPageCount}
                  totalItems={sourceGroups.length}
                  pageSize={GROUP_PAGE_SIZE}
                  onPageChange={setGroupPage}
                />
                <div className="idle-pager-note">
                  Paged by stack: {sourceGroups.length} group
                  {sourceGroups.length !== 1 ? 's' : ''} hold the {totalFiltered} resource
                  {totalFiltered !== 1 ? 's' : ''} in this tab. Click a stack to list its resources.
                </div>
              </>
            )
          ) : (
            <Pagination
              page={currentPage}
              pageCount={totalPages}
              totalItems={totalFiltered}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}

          {groupBy === 'source' && cleanStacks.length > 0 && (
            <div className="idle-clean-panel">
              <button
                type="button"
                className="idle-clean-toggle"
                onClick={() => setShowCleanStacks((v) => !v)}
                aria-expanded={showCleanStacks}
              >
                {showCleanStacks ? '▾' : '▸'} {cleanStacks.length} stack
                {cleanStacks.length !== 1 ? 's' : ''} with nothing idle
              </button>
              {showCleanStacks && (
                <div className="idle-clean-grid">
                  {cleanStacks.map((s) => (
                    <span
                      key={s.stackName}
                      className="idle-clean-chip"
                      title={`${s.total} resource${s.total !== 1 ? 's' : ''}, none idle${s.regions.length ? ` · ${s.regions.join(', ')}` : ''}`}
                    >
                      📦 {s.stackName}
                      <span className="idle-clean-chip-count">0/{s.total}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default IdleResourcesSection;
